import React, { memo, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@chakra-ui/react";
import { isEqual } from "lodash";
import useDebounce from "../../../_hooks/useDebounce";
import usePostFilePicker, {
  getPhotoAllowedFileTypes,
  getPhotoMaxSize,
} from "../PostFilePicker/usePostFilePicker";
import useScraper from "../PostLinkPreview/useScraper";
import PostFilePickerModal from "../PostFilePicker/PostFilePickerModal";
import PostLinkPreview from "../PostLinkPreview/PostLinkPreview";

const PostCarouselItem = ({ data, onChange, onRemove }) => {
  const [loading, setLoading] = useState();
  const [itemData, setItemData] = useState({ ...data });
  const debouncedItemData = useDebounce(itemData, 100);
  const [addFile, setAddFile] = useState(null);
  const [filePickerOpen, setFilePickerOpen] = useState();
  const { fetchScraperData } = useScraper();
  const { uppy, uppyPlugins, uppyNote } = usePostFilePicker({
    id: "carousel-editor",
    restrictions: {
      maxFileSize: getPhotoMaxSize(),
      maxNumberOfFiles: 1,
      allowedFileTypes: getPhotoAllowedFileTypes(),
    },
  });

  const handleValueChange = useCallback(
    (name) => (value) => {
      setItemData({
        ...itemData,
        [name]: value,
      });
    },
    [itemData, setItemData]
  );

  const handleLinkChange = useCallback(
    async (link) => {
      setLoading(true);

      const fetchedData = await fetchScraperData(link);

      setItemData({
        link,
        title: fetchedData?.title || "",
        description: fetchedData?.description || "",
        image: fetchedData?.images?.[0] || "",
      });

      setLoading(false);
    },
    [setLoading, setItemData, fetchScraperData]
  );

  const handleRemove = useCallback(() => {
    onRemove?.();
  }, [onRemove]);

  const handleImageAdd = useCallback(() => {
    setFilePickerOpen(true);
  }, [setFilePickerOpen]);

  const handleCloseFilePicker = useCallback(() => {
    setFilePickerOpen(false);
  }, [setFilePickerOpen]);

  useEffect(() => {
    const onUppyFilesAdded = (files) => {
      if (files.length) {
        setLoading(true);
        setFilePickerOpen(false);
      }
    };

    const onUppyFileRemoved = () => {
      if (!uppy.getFiles().length) {
        setLoading(false);
      }
    };

    const onUppyUploadSuccess = (file, { body }) => {
      setAddFile({
        image: body.url,
      });

      uppy.removeFile(file.id);
    };

    const onUppyUploadError = (file) => {
      uppy.removeFile(file.id);
    };

    const onUppyThumbnailGenerated = (file, preview) => {
      setAddFile({
        preview,
      });
    };

    uppy
      .on("files-added", onUppyFilesAdded)
      .on("file-removed", onUppyFileRemoved)
      .on("upload-success", onUppyUploadSuccess)
      .on("upload-error", onUppyUploadError)
      .on("thumbnail:generated", onUppyThumbnailGenerated);

    return () => {
      uppy
        .off("files-added", onUppyFilesAdded)
        .off("file-removed", onUppyFileRemoved)
        .off("upload-success", onUppyUploadSuccess)
        .off("upload-error", onUppyUploadError)
        .off("thumbnail:generated", onUppyThumbnailGenerated);
    };
  }, [uppy]);

  useEffect(() => {
    if (addFile?.image) {
      handleValueChange("image")(addFile.image);
      setAddFile(null);
    }
  }, [addFile]);

  useEffect(() => {
    setItemData({ ...data });
  }, [data]);

  useEffect(() => {
    if (!isEqual(data, itemData)) {
      onChange?.({ ...itemData });
    }
  }, [debouncedItemData]);

  return (
    <Box>
      <PostLinkPreview
        link={itemData.link}
        title={itemData.title}
        description={itemData.description}
        image={loading && addFile?.preview ? addFile.preview : itemData.image}
        loading={loading}
        onRemove={handleRemove}
        onLinkChange={handleLinkChange}
        onTitleChange={handleValueChange("title")}
        onDescriptionChange={handleValueChange("description")}
        onImageAdd={handleImageAdd}
        onImageChange={handleValueChange("image")}
      />
      {filePickerOpen ? (
        <PostFilePickerModal
          onClose={handleCloseFilePicker}
          uppy={uppy}
          plugins={uppyPlugins}
          note={uppyNote}
        />
      ) : null}
    </Box>
  );
};

PostCarouselItem.propTypes = {
  data: PropTypes.shape({
    link: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
};

PostCarouselItem.defaultProps = {
  data: undefined,
  onRemove: undefined,
};

export default memo(PostCarouselItem);
